import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Intercom from "@intercom/messenger-js-sdk";
import { useAuth } from "../contexts/AuthContext";
import { safeGetItem, safeSetItem } from "../utils/localStorageUtils";
import { useAppUpdates, useFeedbackHelpers } from "../hooks/useFeedbackData";
import { HiChevronLeft } from "react-icons/hi2";
import { useSubscription } from "../providers/SubscriptionProvider";
import { getFunctions, httpsCallable } from "firebase/functions";
// import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
// import CryptoJS from "crypto-js";
export default function Support() {
  const { subscription } = useSubscription();
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Fetch app updates
  const { updates, loading: updatesLoading } = useAppUpdates();

  const { formatDate } = useFeedbackHelpers();

  // Add iOS-specific styles to prevent auto-zoom in Intercom
  useEffect(() => {
    // Inject CSS to prevent iOS auto-zoom in Intercom widget
    const style = document.createElement("style");
    style.id = "intercom-ios-fix";
    style.textContent = `
      /* iOS Safari auto-zoom prevention for Intercom */
      @supports (-webkit-touch-callout: none) {
        /* Target all Intercom input elements */
        iframe[name*="intercom"] input,
        iframe[name*="intercom"] textarea,
        iframe[id*="intercom"] input,
        iframe[id*="intercom"] textarea,
        .intercom-composer input,
        .intercom-composer textarea,
        .intercom-messenger input,
        .intercom-messenger textarea {
          font-size: 16px !important;
          -webkit-text-size-adjust: 100% !important;
        }
      }
    `;

    // Add the style to the document head
    if (!document.getElementById("intercom-ios-fix")) {
      document.head.appendChild(style);
    }

    // Cleanup function to remove the style when component unmounts
    return () => {
      const existingStyle = document.getElementById("intercom-ios-fix");
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  useEffect(() => {
    // console.log("Support page mounted - initializing Intercom...");
    // console.log("Current user:", currentUser);

    // Initialize Intercom with the launcher visible only on this page
    const initIntercom = async () => {
      try {
        // Prepare user data for Intercom
        const intercomConfig: any = {
          app_id: "wiedyikd",
          hide_default_launcher: false, // Show launcher on Support page
        };

        // Add user identification if user is logged in
        if (currentUser) {
          intercomConfig.user_id = currentUser.uid; // Firebase UID as unique identifier
          intercomConfig.email = currentUser.email;
          intercomConfig.name =
            currentUser.displayName ||
            currentUser.email?.split("@")[0] ||
            "User";

          // Add additional user metadata
          intercomConfig.created_at = Math.floor(
            new Date(
              currentUser.metadata.creationTime || Date.now(),
            ).getTime() / 1000,
          );
          // Custom attributes (optional)
          // Subscription custom attributes — inline, not nested
          intercomConfig.subscription_type = subscription?.subscriptionType ?? "default";
          intercomConfig.subscription_via = subscription?.subscriptionVia ?? "default";
          intercomConfig.has_subscription =
            !!subscription &&
            subscription.subscriptionType !== "default" &&
            subscription.subscriptionVia !== "default";
          intercomConfig.custom_attributes = {
            user_type: "authenticated",
            signup_date: currentUser.metadata.creationTime,
            last_login: currentUser.metadata.lastSignInTime,
            email_verified: currentUser.emailVerified,

          };
          // console.log("value we have in in intercom function ", subscription)

          // console.log("Initializing Intercom with user data:", {
          //   user_id: intercomConfig.user_id,
          //   email: intercomConfig.email,
          //   name: intercomConfig.name,
          // });
        } else {
          // For anonymous users, create a temporary identifier
          const anonymousId =
            safeGetItem("intercom_anonymous_id") ||
            `anonymous_${Date.now()}_${Math.random()
              .toString(36)
              .substring(2, 11)}`;
          safeSetItem("intercom_anonymous_id", anonymousId);

          intercomConfig.user_id = anonymousId;
          intercomConfig.name = "Anonymous User";
          intercomConfig.custom_attributes = {
            user_type: "anonymous",
          };

          // console.log(
          //   "Initializing Intercom with anonymous user:",
          //   anonymousId,
          // );
        }

        Intercom(intercomConfig);
        if (currentUser && window.Intercom) {
          window.Intercom("update", {
            subscription_type: subscription?.subscriptionType ?? "default",
            subscription_level: subscription?.subscriptionType === "plus" ? "Plus"
              : subscription?.subscriptionType === "premium" || subscription?.subscriptionType === "platinum" ? "Premium"
              : subscription?.subscriptionType === "coach" ? "Coach"
              : "Free",
          });
        }

        setIntercomLoaded(true);
        // console.log("Intercom initialized successfully on Support page");

        // Apply iOS zoom prevention to Intercom after it loads
        setTimeout(() => {
          // Find all Intercom iframes and apply styles
          const intercomIframes = document.querySelectorAll(
            'iframe[name*="intercom"], iframe[id*="intercom"]',
          );
          intercomIframes.forEach((iframe: any) => {
            try {
              // Add inline styles to prevent zoom
              iframe.style.cssText += `
                -webkit-overflow-scrolling: touch;
                touch-action: manipulation;
              `;
            } catch (error) {
              // Cross-origin restrictions may prevent this
              console.log("Could not apply styles to Intercom iframe:", error);
            }
          });
        }, 500);

        // Auto-show the messenger after a brief delay
        setTimeout(() => {
          if (window.Intercom) {
            window.Intercom("show");
            // console.log("Intercom messenger shown");
          }
        }, 1000);
      } catch (error) {
        console.error("Error initializing Intercom:", error);
      }
    };

    initIntercom();

    // Cleanup: Hide Intercom when leaving the Support page
    return () => {
      // console.log("Leaving Support page - hiding Intercom...");
      if (window.Intercom) {
        try {
          window.Intercom("hide");
          // Update settings to hide launcher globally
          window.Intercom("update", {
            hide_default_launcher: true,
          });
        } catch (error) {
          console.error("Error hiding Intercom:", error);
        }
      }
    };
  }, [currentUser, subscription]); // Re-run when user changes

  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    } else {
      console.warn("Intercom not loaded yet");
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-5xl mx-auto p-8 py-24">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            aria-label="Go back"
          >
            <HiChevronLeft className="h-6 w-6 text-base-content" />
          </button>
          <h2 className="text-2xl font-semibold text-base-content ml-4">
            Support & App Updates
          </h2>
        </div>
        <div className="space-y-6">
          <div className="bg-base-100 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-base-content mb-3">
              Chat Support, Tickets & Help Docs
            </h3>
            <p className="text-base-content/80 mb-4 text-sm">
              The chat widget should appear automatically, or you can click the
              button below. We aim to get back to you within 24 hours. In this
              widget you can:
            </p>
            <ul className="pl-4 text-sm mb-4">
              <li>+ Chat with the support team</li>
              <li>+ Submit a Ticket</li>
              <li>+ Explore Help Articles</li>
            </ul>

            <button
              onClick={openChat}
              disabled={!intercomLoaded}
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                intercomLoaded
                  ? "bg-base-300 hover:bg-base-content/40 text-base-content"
                  : "bg-base-300 text-base-content/60 cursor-not-allowed"
              }`}
            >
              {intercomLoaded ? "Open Chat" : "Loading Chat..."}
            </button>

            {!intercomLoaded && (
              <p className="text-sm text-base-content/60 mt-2">
                Chat support is loading, please wait...
              </p>
            )}
          </div>

          {/* Latest Updates Section */}
          <div className="bg-base-100 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-0">
              <h3 className="text-lg font-semibold text-base-content">
                Latest Updates ({updates.length})
              </h3>
              {updatesLoading && (
                <div className="loading loading-spinner loading-sm"></div>
              )}
            </div>
            {updates.length > 0 ? (
              <>
                {/* Latest update - always visible */}
                <div className="space-y-2 bg-blue-500/10 p-4 rounded-lg mt-4">
                  <div>
                    <span className="text-xs text-base-content/80 font-bold min-w-[120px]">
                      {formatDate(updates[0].createdAt)}
                    </span>
                    <p className="text-sm whitespace-pre-line">
                      {updates[0].message}
                    </p>
                  </div>
                </div>

                {/* Older updates - shown when expanded, scrollable */}
                {showAllUpdates && updates.length > 1 && (
                  <div className="max-h-80 overflow-y-auto space-y-2 mt-2 pr-1">
                    {updates.slice(1).map((update) => (
                      <div
                        key={update.id}
                        className="bg-base-200/50 p-4 rounded-lg"
                      >
                        <span className="text-xs text-base-content/80 font-bold min-w-[120px]">
                          {formatDate(update.createdAt)}
                        </span>
                        <p className="text-sm whitespace-pre-line">
                          {update.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show More / Show Less button */}
                {updates.length > 1 && (
                  <button
                    onClick={() => setShowAllUpdates(!showAllUpdates)}
                    className="mt-3 text-sm font-medium text-base-content/70 hover:text-base-content transition-colors"
                  >
                    {showAllUpdates
                      ? "Show Less"
                      : `Show More (${updates.length - 1} older updates)`}
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-base-content/70">
                No updates posted yet.
              </p>
            )}
          </div>

          <div className="bg-base-100 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-base-content mb-3">
              Public Feedback Board
            </h3>
            <p className="text-base-content/70 mb-4 text-sm">
              We have closed the public feedback board as we were receiving
              messages from too many sources to manage swiftly. You can view the
              public board here.
            </p>

            <button
              onClick={() => navigate("/updates")}
              className="px-6 py-3 rounded-full font-medium transition-colors bg-base-300 hover:bg-base-content/40 text-base-content"
            >
              View Previous Feedback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
